import { useContext, useState } from "react";
import { GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../context/i18n.context";
import SmartFeedback from "../components/SmartFeedback";
import {
  EmptyState,
  BeneficiaryList,
  BeneficiaryForm,
  ValidationBar,
  Beneficiary,
} from "../components/beneficiaryManager";

interface BeneficiaryManagerProps {
  userName: string;
  onBack: () => void;
}

const BeneficiaryManager = ({ userName, onBack }: BeneficiaryManagerProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);

  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [editing, setEditing] = useState<Beneficiary | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const totalPercentage = beneficiaries.reduce((sum, b) => sum + b.percentage, 0);
  const isEmpty = beneficiaries.length === 0;

  const closeForm = () => {
    setEditing(null);
    setShowForm(false);
  };

  const handleAdd = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleEdit = (b: Beneficiary) => {
    setEditing(b);
    setShowForm(true);
  };

  const handleSaveForm = (value: Omit<Beneficiary, "id"> & { id?: string }) => {
    if (value.id) {
      setBeneficiaries((prev) =>
        prev.map((b) => (b.id === value.id ? { ...b, ...value, id: value.id! } : b))
      );
    } else {
      setBeneficiaries((prev) => [
        ...prev,
        { ...value, id: Date.now().toString() },
      ]);
    }
    closeForm();
  };

  const handleDelete = (id: string) => {
    setBeneficiaries((prev) => prev.filter((b) => b.id !== id));
    setDeleteConfirm(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 720 }}>
      {/* Back button + Header */}
      <div>
        <button
          onClick={onBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "#6b7280",
            fontSize: 14,
            fontWeight: 500,
            padding: 0,
            marginBottom: 12,
          }}
        >
          <GlobalIcon iconName="ChevronDown" size="XS" color="#6b7280" />
          <span>{translate("newDashboard.beneficiaryManager.back")}</span>
        </button>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 }}>
          {translate("newDashboard.beneficiaryManager.title")}
        </h1>
        <p style={{ fontSize: 15, color: "#6b7280", marginTop: 4 }}>
          {translate("newDashboard.beneficiaryManager.subtitle")}
        </p>
      </div>

      {isEmpty && !showForm && <EmptyState onAdd={handleAdd} />}

      {!isEmpty && (
        <BeneficiaryList
          beneficiaries={beneficiaries}
          deleteConfirmId={deleteConfirm}
          showForm={showForm}
          onEdit={handleEdit}
          onRequestDelete={setDeleteConfirm}
          onConfirmDelete={handleDelete}
          onCancelDelete={() => setDeleteConfirm(null)}
          onAdd={handleAdd}
        />
      )}

      {showForm && (
        <BeneficiaryForm
          editing={editing}
          onSave={handleSaveForm}
          onCancel={closeForm}
        />
      )}

      {!isEmpty && (
        <ValidationBar
          totalPercentage={totalPercentage}
          onSave={() => {
            // TODO: API call to save beneficiaries
            setShowFeedback(true);
          }}
        />
      )}

      {showFeedback && (
        <SmartFeedback
          userName={userName}
          onClose={() => setShowFeedback(false)}
        />
      )}
    </div>
  );
};

export default BeneficiaryManager;
